import { useState } from 'react';
import type { TFunction } from 'i18next';
import { changeOrderStatus, type OrderStatusChangeResult } from './api/orderStatusApi';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';

export interface OrderStatusFormValues {
  status: string;
  notes?: string;
  /** Recipient choice of the confirmation dialog (decision 2026-10-04). */
  confirmationNotice?: { customer: boolean; operationsUserIds: string[]; adminUserIds: string[] };
  /** „Skąd realizujemy?” of the confirmation dialog (owner 2026-10-05). */
  fulfillmentSource?: 'WAREHOUSE' | 'FACTORY_DIRECT';
  fulfillmentNote?: string;
  /** Delivery day `YYYY-MM-DD` chosen in „Potwierdź dostarczenie” (DELIVERED only). */
  actualDelivery?: string;
}

interface UseOrderStatusChangeWithConfirmationOptions {
  orderId?: string | null;
  /** Receives the saved status and any non-blocking operational context. */
  onSuccess: (values: OrderStatusFormValues, result: OrderStatusChangeResult) => void | Promise<void>;
  onError: (error: unknown) => void;
  /**
   * `modal` from antd's `App.useApp()` plus `t`: DELIVERED without a released
   * WZ is a consistency warning (decision 2026-09-18) confirmed with a reason
   * through the shared operational-exception modal. Without them the
   * warning surfaces as a plain error.
   */
  confirmation?: { modal: Parameters<typeof submitWithOperationalException>[2]; t: TFunction };
}

export function useOrderStatusChangeWithConfirmation({
  orderId,
  onSuccess,
  onError,
  confirmation,
}: UseOrderStatusChangeWithConfirmationOptions) {
  const [submitting, setSubmitting] = useState(false);

  const submitStatusChange = async (values: OrderStatusFormValues) => {
    if (!orderId) return;

    setSubmitting(true);
    try {
      const result = confirmation
        ? await submitWithOperationalException(
          (payload) => changeOrderStatus(orderId, payload as unknown as OrderStatusFormValues),
          values as unknown as Record<string, unknown>,
          confirmation.modal,
          confirmation.t,
        )
        : await changeOrderStatus(orderId, values);
      if (!result) return;
      await onSuccess(values, result);
    } catch (error: any) {
      onError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    submitting,
    submitStatusChange,
  };
}
